import type { TFunction } from 'i18next';
import { KeyRound, Send, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  EntityFacts,
  EntityHead,
  EntityTimeline,
  RelatedSection,
  type TimelineEvent,
} from '@/components/entity-sheet/EntityParts';
import { Button } from '@/components/ui/button';
import { formatDate, formatDateTime } from '@/lib/intl';
import { cn } from '@/lib/utils';
import type { useAccessActions } from '../people/person-actions';
import { useRoleText } from '../roles/role-text';
import { Avatar } from '../ui/person-bits';
import { useWhenText } from './InvitationItem';
import { INVITATION_TONE, type Invitation } from './use-invitations';

type Translate = TFunction<'administration'>;

/** The fact that closes the board: when it expires, expired, or was resolved. */
function endingFact(t: Translate, invitation: Invitation): { label: string; date: string } | null {
  const { status, resolvedAt } = invitation;
  if (status === 'pending') {
    return { label: t('invitations.card.facts.expires'), date: invitation.expiresAt };
  }
  if (status === 'expired') {
    return { label: t('invitations.card.facts.expired'), date: invitation.expiresAt };
  }
  if (resolvedAt === null) return null;
  return {
    label: t(
      status === 'accepted'
        ? 'invitations.card.facts.resolvedAccepted'
        : 'invitations.card.facts.resolvedWithdrawn',
    ),
    date: resolvedAt,
  };
}

/** The last event of the story, by the invitation's state. */
function lastEvent(t: Translate, invitation: Invitation): TimelineEvent {
  const { status, resolvedAt } = invitation;
  if (status === 'pending') {
    return {
      key: 'waiting',
      text: t('invitations.card.event.waiting'),
      when: t('invitations.card.eventWaiting', { date: formatDate(invitation.expiresAt) }),
      tone: 'muted',
    };
  }
  if (status === 'expired') {
    return {
      key: 'expired',
      text: t('invitations.card.event.expired'),
      when: formatDateTime(invitation.expiresAt),
      tone: 'warning',
    };
  }
  return {
    key: status,
    text: t(`invitations.card.event.${status}`),
    when: resolvedAt !== null ? formatDateTime(resolvedAt) : '',
    tone: status === 'accepted' ? 'success' : 'muted',
  };
}

function storyEvents(t: Translate, invitation: Invitation): TimelineEvent[] {
  return [
    {
      key: 'sent',
      text: t('invitations.card.event.sent'),
      when: `${formatDateTime(invitation.createdAt)} · ${invitation.invitedBy}`,
    },
    lastEvent(t, invitation),
  ];
}

function CardActions({
  invitation,
  actions,
  canWithdraw,
  canRenew,
}: {
  readonly invitation: Invitation;
  readonly actions: ReturnType<typeof useAccessActions>;
  readonly canWithdraw: boolean;
  readonly canRenew: boolean;
}) {
  const { t } = useTranslation('administration');
  const withdrawing = actions.isPending(`withdraw:${invitation.id}`);
  const renewing = actions.isPending(`renew:${invitation.id}`);
  return (
    <>
      {canWithdraw ? (
        <Button
          variant="outline"
          size="sm"
          disabled={withdrawing}
          onClick={() => void actions.withdraw(invitation)}
        >
          {t('invitations.withdraw')}
        </Button>
      ) : null}
      {canRenew ? (
        <Button
          variant="outline"
          size="sm"
          disabled={renewing}
          onClick={() => void actions.renew(invitation)}
        >
          <Send />
          {t('invitations.renew')}
        </Button>
      ) : null}
    </>
  );
}

function PersonSection({
  invitation,
  titleId,
  placeName,
  peoplePath,
}: {
  readonly invitation: Invitation;
  readonly titleId: string;
  readonly placeName: string;
  readonly peoplePath: string;
}) {
  const { t } = useTranslation('administration');
  return (
    <RelatedSection
      id={`${titleId}-person`}
      title={t('invitations.card.person')}
      count={1}
      rows={[
        {
          key: 'person',
          to: `${peoplePath}?q=${encodeURIComponent(invitation.email)}`,
          label: invitation.displayName ?? invitation.email,
          sub: t('invitations.card.personSub', { place: placeName }),
          lead: (
            <UserRound aria-hidden="true" className="text-muted-foreground size-4.5 flex-none" />
          ),
        },
      ]}
    />
  );
}

function RolesSection({
  invitation,
  titleId,
  rolesPath,
}: {
  readonly invitation: Invitation;
  readonly titleId: string;
  readonly rolesPath: string;
}) {
  const { t } = useTranslation('administration');
  const { name, description } = useRoleText();
  return (
    <RelatedSection
      id={`${titleId}-roles`}
      title={t('invitations.card.roles')}
      count={invitation.roles.length}
      rows={invitation.roles.map((role) => ({
        key: role.id,
        to: `${rolesPath}?role=${encodeURIComponent(role.id)}`,
        label: name(role),
        sub: description(role),
        lead: <KeyRound aria-hidden="true" className="text-muted-foreground size-4.5 flex-none" />,
      }))}
      more={{ to: rolesPath, label: t('invitations.card.rolesMore') }}
    />
  );
}

export function InvitationCard({
  invitation,
  titleId,
  placeName,
  rolesPath,
  peoplePath,
  actions,
}: {
  readonly invitation: Invitation;
  readonly titleId: string;
  readonly placeName: string;
  readonly rolesPath: string;
  readonly peoplePath: string;
  readonly actions: ReturnType<typeof useAccessActions>;
}) {
  const { t } = useTranslation('administration');
  const { status } = invitation;

  const when = useWhenText(invitation);

  const ending = endingFact(t, invitation);

  const story = storyEvents(t, invitation);

  const canWithdraw = status === 'pending' && invitation.can.withdraw;
  const canRenew = invitation.can.renew && status !== 'accepted';

  return (
    <div className="flex flex-col gap-4">
      <EntityHead
        titleId={titleId}
        kind={t('invitations.card.kind')}
        title={invitation.email}
        sub={invitation.displayName ?? t('people.noName')}
        lead={<Avatar name={invitation.email} invitation />}
        status={
          <>
            <span className={cn('status-badge', INVITATION_TONE[status])}>
              {t(`invitations.status.${status}`)}
            </span>
            {when !== null ? <span className="entity-when">{when}</span> : null}
          </>
        }
        actions={
          canWithdraw || canRenew ? (
            <CardActions
              invitation={invitation}
              actions={actions}
              canWithdraw={canWithdraw}
              canRenew={canRenew}
            />
          ) : undefined
        }
      />
      <EntityFacts
        facts={[
          { label: t('invitations.card.facts.sent'), value: formatDate(invitation.createdAt) },
          { label: t('invitations.card.facts.by'), value: invitation.invitedBy },
          ...(ending !== null ? [{ label: ending.label, value: formatDate(ending.date) }] : []),
          { label: t('invitations.card.facts.where'), value: invitation.place.name },
        ]}
      />
      <RolesSection invitation={invitation} titleId={titleId} rolesPath={rolesPath} />
      {status === 'accepted' ? (
        <PersonSection
          invitation={invitation}
          titleId={titleId}
          placeName={placeName}
          peoplePath={peoplePath}
        />
      ) : null}
      <EntityTimeline id={`${titleId}-story`} title={t('invitations.card.story')} events={story} />
    </div>
  );
}
